import qs from 'qs';
import { isObject } from '../../predicates/type';
import { formatFilters } from '../../modules/list';
import { type PreloadApiManager } from './PreloadApiManager';
import { type CustomObject } from '../../types';
import { BuildUrlWithParams, GetWindowUrl } from './types';
import { type Get, NetworkResponse } from '@packages/dataFetch';

export const lowerCaseKeys = <T>(value: T): T => {
	if (isObject(value)) {
		return Object.entries(value).reduce(
			(acc, [key, value]) => ({
				...acc,
				[key.toLowerCase()]: value,
			}),
			{} as T,
		);
	}

	return value;
};

export const buildUrlWithParams: BuildUrlWithParams = (url, params = {}) => {
	const [plainUrl, paramsFromUrl] = url.split('?');

	const mergedParams = {
		...lowerCaseKeys(
			qs.parse(paramsFromUrl, {
				comma: true,
				allowDots: true,
			}),
		),
		...lowerCaseKeys(params),
	};

	const sortedKeys = Object.keys(mergedParams).sort();
	const sortedParams = sortedKeys.reduce((acc, key) => ({ ...acc, [key]: mergedParams[key] }), {});

	const queryString = qs.stringify(sortedParams, {
		arrayFormat: 'comma',
		allowDots: true,
		addQueryPrefix: true,
	});

	return `${plainUrl}${queryString}`;
};

export const getWindowUrl: GetWindowUrl = () => new URL(window.location.href);

export const getListParams = () => {
	const search = getWindowUrl().search;
	const params: CustomObject = qs.parse(search, {
		ignoreQueryPrefix: true,
		comma: true,
		allowDots: true,
	});

	return formatFilters(params);
};

export const buildPreloadApi = ({
	getWindowUrl,
	buildUrlWithParams,
	preloadApiManager,
}: {
	getWindowUrl: GetWindowUrl;
	buildUrlWithParams: BuildUrlWithParams;
	preloadApiManager: PreloadApiManager;
}) => {
	return (
		url: string,
		options: {
			includeParamsFromUrl?: string[];
			includeRouteId?: boolean;
		} = {},
	) => {
		const { includeParamsFromUrl = [], includeRouteId } = options;

		const windowUrl = getWindowUrl();

		if (includeRouteId) {
			const id = windowUrl.pathname.split('/').pop();

			if (!id) {
				throw new Error('Id not found');
			}

			url = `${url}/${id}`;
		}

		const params = includeParamsFromUrl.reduce((acc, keyToInclude) => {
			windowUrl.searchParams.forEach((value, key) => {
				if (key.toLowerCase() === keyToInclude.toLowerCase()) {
					acc[key] = value;
				}
			});

			return acc;
		}, {});

		const urlWithParams = buildUrlWithParams(url, params);

		return preloadApiManager.preload.call(preloadApiManager, urlWithParams);
	};
};

export const buildPreloadListData = ({
	getListParams,
	preloadApiManager,
	buildUrlWithParams,
}: {
	getListParams: () => CustomObject;
	preloadApiManager: PreloadApiManager;
	buildUrlWithParams: BuildUrlWithParams;
}) => {
	return (url: string) => {
		const params = getListParams();
		const urlWithParams = buildUrlWithParams(url, params);

		return preloadApiManager.preload.call(preloadApiManager, urlWithParams);
	};
};

export const buildPreloadedGET = ({
	apiCallback,
	buildUrlWithParams,
	preloadApiManager,
}: {
	apiCallback: Get;
	buildUrlWithParams: BuildUrlWithParams;
	preloadApiManager: PreloadApiManager;
}): Get => {
	return <T = unknown>(
		/**
		 * Has to match the case with the preloaded query URL
		 */
		url,
		config,
	) => {
		const urlWithParams = buildUrlWithParams(url, config?.params);
		const preloaded = preloadApiManager.pull<NetworkResponse<T>>(urlWithParams);

		if (preloaded) return preloaded;

		return apiCallback<T>(url, config);
	};
};
